import { useState } from 'react';
import PropTypes from 'prop-types';

import { confirmCollectionReceipt } from '#/api/crmMgmt/customerOrder/collection';
import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  buildDefaultCredits,
  compactCredits,
  validateCredits,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import CollectionCreditsEditor from './CollectionCreditsEditor';
import useCollectionMutation from './useCollectionMutation';

/** 確認實收：預設依期別順序抵付；同視窗重送沿用同一把 Idempotency-Key。 */
const CollectionReceiptConfirmDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  receipt,
  summaryPhases,
}) => {
  const [credits, setCredits] = useState(() => buildDefaultCredits(receipt.amount, summaryPhases));
  const [formError, setFormError] = useState(null);

  const mutation = useCollectionMutation(
    customerOrderId,
    (payload, { idempotencyKey }) => confirmCollectionReceipt(
      customerOrderId,
      receipt.id,
      payload,
      { idempotencyKey },
    ),
    { successMessage: '已確認實收', onSuccess: () => onOpenChange(false) },
  );

  function handleConfirm() {
    const error = validateCredits(receipt.amount, credits, summaryPhases);
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({ expectedVersionNo: receipt.versionNo, credits: compactCredits(credits) });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="確認實收"
      description={
        `金額 ${formatDisplayAmount(receipt.amount)}・收款時間 ${formatDateTimeOrDash(receipt.actualReceivedAt)}。`
        + '確認後不可修改，錯誤只能沖銷。'
      }
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '確認中…' : '確認實收'}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <CollectionCreditsEditor
          summaryPhases={summaryPhases}
          credits={credits}
          onChange={setCredits}
          idPrefix="collection-confirm-credit"
        />
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionReceiptConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  receipt: PropTypes.object.isRequired,
  summaryPhases: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CollectionReceiptConfirmDialog;
